import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Bell,
  ShoppingCart,
  CreditCard,
  CalendarCheck,
  Info,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Check,
  CheckCheck,
  Trash2,
  ExternalLink,
} from "lucide-react";
import {
  useNotifications,
  Notification,
} from "@/rc/hooks/notifications/use-notifications";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";

const iconMap = {
  ShoppingCart,
  CreditCard,
  CalendarCheck,
  Info,
  CheckCircle2,
  AlertTriangle,
  XCircle,
};

/** Map `notifications.type` (DB) to lucide icon keys — table has no `icon` column. */
const NOTIFICATION_TYPE_TO_ICON: Partial<
  Record<string, keyof typeof iconMap>
> = {
  order: "ShoppingCart",
  payment: "CreditCard",
  reservation: "CalendarCheck",
  loyalty: "CheckCircle2",
  success: "CheckCircle2",
  warning: "AlertTriangle",
  error: "XCircle",
  info: "Info",
};

function notificationIconKey(type: string | null): keyof typeof iconMap {
  if (!type) return "Info";
  return NOTIFICATION_TYPE_TO_ICON[type] ?? "Info";
}

/** Derive in-app link from `type` + `reference_id` — table has no `link` column. */
function notificationLink(n: Notification): string | null {
  if (!n.reference_id) return null;
  switch (n.type) {
    case "order":
    case "payment":
      return "/admin/orders";
    case "reservation":
      return "/admin/reservations";
    case "loyalty":
      return "/admin/loyalty/push";
    default:
      return null;
  }
}

const typeStyles: Record<string, string> = {
  order: "text-[hsl(var(--terminal-info))] bg-[hsl(var(--terminal-info)/0.1)]",
  payment:
    "text-[hsl(var(--terminal-success))] bg-[hsl(var(--terminal-success)/0.1)]",
  reservation:
    "text-[hsl(var(--terminal-parked))] bg-[hsl(var(--terminal-parked)/0.1)]",
  success:
    "text-[hsl(var(--terminal-success))] bg-[hsl(var(--terminal-success)/0.1)]",
  warning:
    "text-[hsl(var(--terminal-warning))] bg-[hsl(var(--terminal-warning)/0.1)]",
  error: "text-destructive bg-destructive/10",
  info: "text-primary bg-primary/10",
};

function NotificationItem({
  notification,
  onRead,
  onDelete,
  onNavigate,
}: {
  notification: Notification;
  onRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (link: string) => void;
}) {
  const { t } = useTranslation("admin");
  const IconComponent =
    iconMap[notificationIconKey(notification.type)] || Info;
  const colorClass =
    typeStyles[notification.type ?? "info"] || typeStyles.info;
  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
    locale: sv,
  });

  return (
    <div
      className={cn(
        "group flex gap-3 px-4 py-3 transition-colors hover:bg-accent/40 cursor-pointer relative",
        !notification.is_read && "bg-primary/[0.04]"
      )}
      onClick={() => {
        if (!notification.is_read) {
          onRead(notification.id);
          return;
        }
        const link = notificationLink(notification);
        if (link) onNavigate(link);
      }}
    >
      {!notification.is_read && (
        <div className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-primary" />
      )}

      <div className={cn("mt-0.5 p-1.5 rounded-lg shrink-0", colorClass)}>
        <IconComponent className="w-3.5 h-3.5" />
      </div>

      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-xs leading-snug text-foreground",
            !notification.is_read ? "font-semibold" : "font-medium"
          )}
        >
          {notification.title}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-[10px] text-muted-foreground/60 mt-1">{timeAgo}</p>
      </div>

      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        {!notification.is_read && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              onRead(notification.id);
            }}
            title={t("notifications.markAsRead")}
          >
            <Check className="w-3 h-3" />
          </Button>
        )}
        {notificationLink(notification) && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(notificationLink(notification)!);
            }}
            title={t("notifications.open")}
          >
            <ExternalLink className="w-3 h-3" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-destructive hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
          title={t("notifications.delete")}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

export function NotificationCenter() {
  const { t } = useTranslation("admin");
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleNavigate = (link: string) => {
    setOpen(false);
    navigate(link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-[18px] min-w-[18px] flex items-center justify-center p-0 text-[10px] tabular-nums rounded-full"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(400px,calc(100vw-2rem))] p-0 rounded-xl shadow-lg border border-border"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-sm text-foreground">
              {t("notifications.title")}
            </h4>
            {unreadCount > 0 && (
              <Badge
                variant="secondary"
                className="text-[10px] h-5 px-1.5 rounded-full"
              >
                {t("notifications.newCount", { count: unreadCount })}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 gap-1 text-muted-foreground hover:text-foreground"
                onClick={markAllAsRead}
              >
                <CheckCheck className="w-3 h-3" />
                {t("notifications.readAll")}
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 gap-1 text-muted-foreground hover:text-destructive"
                onClick={clearAll}
              >
                <Trash2 className="w-3 h-3" />
                {t("notifications.clearAll")}
              </Button>
            )}
          </div>
        </div>
        <Separator />

        {/* List */}
        <ScrollArea className="max-h-[min(480px,60vh)]">
          {loading ? (
            <div className="py-8 text-center text-xs text-muted-foreground">
              {t("notifications.loading")}
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-12 text-center px-4">
              <Bell className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
              <p className="text-sm font-medium text-muted-foreground">
                {t("notifications.empty")}
              </p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                {t("notifications.emptyDesc")}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={markAsRead}
                  onDelete={deleteNotification}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
